import React, { useState } from "react";
import { IoMdClose } from "react-icons/io";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "react-hot-toast";
import { apiUrl } from "../../common/config/apiUrl";

const JobApplicationModal = ({ isOpen, setIsOpen, titleStore }) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    mobile: "",
    gender: "",
    location: "",
    cv: null,
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false); 

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    setErrors({ ...errors, [name]: "" });
  };

  const handleFileChange = (e) => {
    setFormData({ ...formData, cv: e.target.files[0] });
    setErrors({ ...errors, cv: "" });
  };

  const validate = () => {
    let newErrors = {};
    if (!formData.name) newErrors.name = "Name is required";
    if (!formData.email) newErrors.email = "Email is required";
    if (!formData.mobile) newErrors.mobile = "Mobile number is required";
    if (!formData.gender) newErrors.gender = "Gender is required";
    if (!formData.location) newErrors.location = "Location is required";
    if (!formData.cv) newErrors.cv = "CV is required";
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = validate();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setLoading(true); 

    const formDataObj = new FormData();
    formDataObj.append("name", formData.name);
    formDataObj.append("email", formData.email);
    formDataObj.append("mobile_number", formData.mobile);
    formDataObj.append("gender", formData.gender);
    formDataObj.append("location", formData.location);
    formDataObj.append("file", formData.cv);
    formDataObj.append("title", titleStore);

    try {
      await axios.post(`${apiUrl}career/store`, formDataObj, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Application submitted successfully!");
      setIsOpen(false);
      setFormData({
        name: "",
        email: "",
        mobile: "",
        gender: "",
        location: "",
        cv: null,
      });
    } catch (error) {
      const errorMessage = error.response?.data?.message || "Failed to submit application";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 flex md:items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm z-50 transition-opacity"
        >
          <motion.div
            initial={{ scale: 0.8 }}
            animate={{ scale: 1 }}
            exit={{ scale: 0.8 }}
            className="bg-white p-4 md:h-auto hide-scrollbar  overflow-y-scroll border-r-8 border-yellow-500 shadow-inner w-full max-w-2xl relative transform scale-95 hover:scale-100 transition-transform duration-300"
          >
            <button
              className="absolute top-3 right-3 text-gray-600 hover:text-red-900"
              onClick={() => setIsOpen(false)}
            >
              <IoMdClose size={24} />
            </button>
            <h2 className="text-2xl font-semibold text-secondary mb-2 text-center">
              Apply for Job
            </h2>
            <div className="underline pb-2">
              <img
                alt="underline"
                src={require("../../common/images/blue-underline.png")}
                className="w-[150px] h-[5px] mx-auto"
              />
            </div>
            <form className="grid grid-cols-1 sm:grid-cols-2 gap-4" onSubmit={handleSubmit}>
              <div>
                <label className="block font-medium pb-2">Name</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Enter your name"
                  className="w-full border p-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-800"
                />
                {errors.name && <p className="text-red-500 text-sm">{errors.name}</p>}
              </div>
              <div>
                <label className="block font-medium pb-2">Email</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Enter your email"
                  className="w-full border p-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-800"
                />
                {errors.email && <p className="text-red-500 text-sm">{errors.email}</p>}
              </div>
              <div>
                <label className="block font-medium pb-2">Mobile Number</label>
                <input
                  type="tel"
                  name="mobile"
                  value={formData.mobile}
                  onChange={handleChange}
                  placeholder="Enter your mobile number"
                  className="w-full border p-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-800"
                />
                {errors.mobile && <p className="text-red-500 text-sm">{errors.mobile}</p>}
              </div>
              <div>
                <label className="block font-medium pb-2">Gender</label>
                <select
                  name="gender"
                  value={formData.gender}
                  onChange={handleChange}
                  className="w-full border p-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-800"
                >
                  <option value="">Select Gender</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Other">Other</option>
                </select>
                {errors.gender && <p className="text-red-500 text-sm">{errors.gender}</p>}
              </div>
              <div>
                <label className="block font-medium pb-2">Location</label>
                <input
                  type="text"
                  name="location"
                  value={formData.location}
                  onChange={handleChange}
                  placeholder="Enter your location"
                  className="w-full border p-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-800"
                />
                {errors.location && <p className="text-red-500 text-sm">{errors.location}</p>}
              </div>
              <div>
                <label className="block font-medium pb-2">Upload CV</label>
                <input
                  type="file"
                  onChange={handleFileChange}
                  className="w-full border p-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-800"
                />
                {errors.cv && <p className="text-red-500 text-sm">{errors.cv}</p>}
              </div>
              <div className="col-span-1 sm:col-span-2 flex justify-center">
                <button
                  type="submit"
                  className="w-[50%] mx-auto bg-secondary text-white py-2 font-semibold hover:bg-blue-800 transition duration-300"
                  disabled={loading}
                >
                  {loading ? "Submitting..." : "Submit"}
                </button>
              </div>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default JobApplicationModal;
